import { useCallback, useEffect, useRef, useState } from "react";
import { FiMinus, FiPlus, FiRefreshCw } from "react-icons/fi";

interface Props {
  src: string | null;
  label: string;
  emptyText?: string;
  /** Optional overlay (e.g. wireframe) that zooms/pans with the base image */
  overlaySrc?: string | null;
  overlayOpacity?: number;
}

/**
 * Compact reference image with scroll-wheel / button zoom and drag pan.
 * Base + optional overlay share one transform (treated as a single image).
 */
export default function ZoomableReferenceImage({
  src,
  label,
  emptyText = "No image",
  overlaySrc = null,
  overlayOpacity = 0.92,
}: Props) {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);

  const reset = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  useEffect(() => {
    reset();
  }, [src, overlaySrc, reset]);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.12 : 0.12;
      setZoom((z) =>
        Math.max(1, Math.min(6, Math.round((z + delta) * 100) / 100))
      );
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const layerTransform = {
    transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
    transformOrigin: "center center" as const,
  };

  return (
    <div className="rounded-xl border border-white/10 bg-[#101211] overflow-hidden flex flex-col min-h-0 h-full">
      <div className="flex items-center justify-between gap-2 px-2.5 py-2 border-b border-white/5 shrink-0">
        <p className="text-xs uppercase tracking-wider text-[#8b8f8d] font-semibold truncate">
          {label}
        </p>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={() =>
              setZoom((z) => Math.max(1, Math.round((z - 0.25) * 100) / 100))
            }
            className="w-6 h-6 rounded-md flex items-center justify-center text-gray-400 hover:bg-white/5 hover:text-[#2fa9bf]"
            aria-label="Zoom out"
          >
            <FiMinus className="w-3 h-3" />
          </button>
          <span className="text-xs font-mono text-[#2fa9bf] w-9 text-center tabular-nums">
            {Math.round(zoom * 100)}%
          </span>
          <button
            type="button"
            onClick={() =>
              setZoom((z) => Math.min(6, Math.round((z + 0.25) * 100) / 100))
            }
            className="w-6 h-6 rounded-md flex items-center justify-center text-gray-400 hover:bg-white/5 hover:text-[#2fa9bf]"
            aria-label="Zoom in"
          >
            <FiPlus className="w-3 h-3" />
          </button>
          <button
            type="button"
            onClick={reset}
            className="w-6 h-6 rounded-md flex items-center justify-center text-gray-400 hover:bg-white/5 hover:text-[#2fa9bf]"
            aria-label="Reset zoom"
          >
            <FiRefreshCw className="w-3 h-3" />
          </button>
        </div>
      </div>

      <div
        ref={viewportRef}
        className="relative flex-1 min-h-[120px] bg-black/40 overflow-hidden cursor-grab active:cursor-grabbing touch-none"
        onPointerDown={(e) => {
          if (!src) return;
          (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
          dragRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            originX: pan.x,
            originY: pan.y,
          };
        }}
        onPointerMove={(e) => {
          const d = dragRef.current;
          if (!d) return;
          setPan({
            x: d.originX + (e.clientX - d.startX),
            y: d.originY + (e.clientY - d.startY),
          });
        }}
        onPointerUp={() => {
          dragRef.current = null;
        }}
        onPointerCancel={() => {
          dragRef.current = null;
        }}
        onDoubleClick={reset}
      >
        {src ? (
          <div
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
            style={layerTransform}
          >
            <div className="relative max-w-full max-h-full">
              <img
                src={src}
                alt={label}
                draggable={false}
                className="block max-w-full max-h-full object-contain select-none"
              />
              {overlaySrc && (
                <img
                  src={overlaySrc}
                  alt=""
                  draggable={false}
                  className="absolute inset-0 w-full h-full object-contain select-none"
                  style={{ opacity: overlayOpacity }}
                />
              )}
            </div>
          </div>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-xs text-gray-600 px-3 text-center">
            {emptyText}
          </div>
        )}
      </div>
    </div>
  );
}
